// Where the camera must sit to see a bundle, from its neutral vertices alone.
// Notes: ~/comments/aos-threejs-poc/framing_neutralBounds_before_the_first_appr.md

import * as THREE from "three";

export interface NeutralBounds {
  centre: [number, number, number];
  radius: number;
}

export interface NeutralBoundsOptions {
  worldScale?: number;
  worldOffset?: readonly [number, number, number];
  /** The training frame's rotation, as the SplatMesh gets it. */
  worldRotation?: THREE.Quaternion | null;
  /** Percentile trim per axis — UV-seam outliers sit far outside the body. */
  percentile?: number;
}

const at = (sorted: number[], fraction: number): number =>
  sorted[Math.min(sorted.length - 1, Math.floor(sorted.length * fraction)) | 0];

/**
 * Sphere enclosing the trimmed extent of `positions` (xyz triples, training frame),
 * placed exactly as `applyMeshPlacement` places the mesh: scale, then rotate, then offset.
 *
 * Null when there are no vertices — a caller must not frame on an invented box.
 */
export function neutralBounds(positions: Float32Array, options: NeutralBoundsOptions = {}): NeutralBounds | null {
  const count = Math.floor(positions.length / 3);
  if (!count) return null;
  const scale = options.worldScale ?? 1;
  const offset = options.worldOffset ?? [0, 0, 0];
  const trim = options.percentile ?? 0.05;

  const xs: number[] = [], ys: number[] = [], zs: number[] = [];
  for (let i = 0; i < count; i++) {
    xs.push(positions[i * 3]);
    ys.push(positions[i * 3 + 1]);
    zs.push(positions[i * 3 + 2]);
  }
  xs.sort((a, b) => a - b); ys.sort((a, b) => a - b); zs.sort((a, b) => a - b);
  const xMin = at(xs, trim), xMax = at(xs, 1 - trim);
  const yMin = at(ys, trim), yMax = at(ys, 1 - trim);
  const zMin = at(zs, trim), zMax = at(zs, 1 - trim);

  const centre = new THREE.Vector3(
    ((xMin + xMax) / 2) * scale,
    ((yMin + yMax) / 2) * scale,
    ((zMin + zMax) / 2) * scale,
  );
  if (options.worldRotation) centre.applyQuaternion(options.worldRotation);
  // The radius is the box's half-diagonal, which turns with the box — no rotation needed.
  const radius = Math.hypot(
    ((xMax - xMin) / 2) * scale,
    ((yMax - yMin) / 2) * scale,
    ((zMax - zMin) / 2) * scale,
  );
  return {
    centre: [centre.x + offset[0], centre.y + offset[1], centre.z + offset[2]],
    radius,
  };
}
